/**
 * Login Microsoft Entra ID (TC_AUTH_PROVIDER=entra) con oidc-client-ts.
 *
 * Flusso: il portale (client pubblico SPA, Authorization Code + PKCE, nessun secret) ottiene un
 * id_token da Entra; lo scambia con il product-service (`POST /auth/exchange`), che ne verifica la
 * firma, mappa gruppi/App Role nei ruoli TC (configurati da /admin) e restituisce il JWT applicativo
 * usato come Bearer verso tutti i servizi. Tenant e client ID arrivano a runtime da
 * `GET /auth/sso-config`: nessuna variabile Vite da ricompilare.
 *
 * Rinnovo: `renewEntraSession()` usa il refresh token Entra (scope `offline_access`) via
 * `signinSilent()` e ripete lo scambio. L'utente oidc-client-ts (con il refresh token) vive in
 * localStorage, come il JWT applicativo (vedi la nota sicurezza in tokenStore.ts).
 *
 * Break-glass: `breakGlassLogin()` per l'admin locale definito da env sul product-service.
 */
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { SERVICE_URLS } from '../api/client.js';
import { getAuthMode, setAuthMode, setToken } from './tokenStore.js';

export interface EntraClientConfig {
  authority: string;
  clientId: string;
  scopes: string[];
}

export interface SsoConfig {
  /** null = login Microsoft non attivo. */
  entra: EntraClientConfig | null;
  /** True se il product-service ha un admin locale di emergenza. */
  breakGlass: boolean;
}

const NO_SSO: SsoConfig = { entra: null, breakGlass: false };

/** Path della callback: va registrato in Entra come Redirect URI della piattaforma "SPA". */
export const ENTRA_CALLBACK_PATH = '/auth/entra/callback';

export function entraRedirectUri(): string {
  return `${window.location.origin}${ENTRA_CALLBACK_PATH}`;
}

export class EntraLoginError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EntraLoginError';
  }
}

const authUrl = (path: string) => `${SERVICE_URLS.product}/auth${path}`;

/**
 * Configurazione pubblica dell'SSO. Endpoint assente (edizione senza broker) o non raggiungibile →
 * nessun SSO: la LoginPage ripiega sulle altre modalità.
 */
export async function fetchSsoConfig(fetchFn: typeof fetch = globalThis.fetch): Promise<SsoConfig> {
  try {
    const res = await fetchFn(authUrl('/sso-config'), { headers: { Accept: 'application/json' } });
    if (!res.ok) return NO_SSO;
    const body = (await res.json()) as Partial<SsoConfig>;
    const e = body.entra;
    return {
      entra:
        e && typeof e.authority === 'string' && typeof e.clientId === 'string'
          ? { authority: e.authority, clientId: e.clientId, scopes: e.scopes ?? ['openid'] }
          : null,
      breakGlass: body.breakGlass === true,
    };
  } catch {
    return NO_SSO;
  }
}

let manager: { key: string; um: UserManager } | null = null;

/** UserManager per la config data (ricreato se tenant/client cambiano da /admin). */
function userManager(cfg: EntraClientConfig): UserManager {
  const key = `${cfg.authority}|${cfg.clientId}|${cfg.scopes.join(' ')}`;
  if (manager?.key !== key) {
    const store = new WebStorageStateStore({ store: window.localStorage });
    manager = {
      key,
      um: new UserManager({
        authority: cfg.authority,
        client_id: cfg.clientId,
        redirect_uri: entraRedirectUri(),
        response_type: 'code',
        scope: cfg.scopes.join(' '),
        userStore: store,
        // Lo stato del redirect resta per-tab (default di oidc-client-ts: sessionStorage).
        automaticSilentRenew: false,
        monitorSession: false,
        loadUserInfo: false,
      }),
    };
  }
  return manager.um;
}

/** Scambia l'id_token Entra con il JWT applicativo e lo salva. */
async function exchange(idToken: string, fetchFn: typeof fetch): Promise<string> {
  const res = await fetchFn(authUrl('/exchange'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken }),
  });
  const body = (await res.json().catch(() => ({}))) as { accessToken?: string; error?: string };
  if (!res.ok || !body.accessToken) {
    throw new EntraLoginError(body.error ?? `Accesso rifiutato (HTTP ${res.status})`);
  }
  setAuthMode('entra');
  setToken(body.accessToken);
  return body.accessToken;
}

/** Avvia il login: redirect a Entra. `returnTo` è la pagina da riaprire dopo la callback. */
export async function startEntraLogin(cfg: EntraClientConfig, returnTo = '/'): Promise<void> {
  await userManager(cfg).signinRedirect({ state: { returnTo } });
}

/**
 * Completa il login sulla pagina di callback: code → token (oidc-client-ts, PKCE e state/nonce
 * verificati da lui), poi scambio con il product-service. Restituisce la pagina da riaprire.
 */
export async function completeEntraLogin(
  fetchFn: typeof fetch = globalThis.fetch,
): Promise<string> {
  const { entra } = await fetchSsoConfig(fetchFn);
  if (!entra) throw new EntraLoginError('Accesso Microsoft non attivo.');
  const user = await userManager(entra).signinRedirectCallback();
  if (!user.id_token) throw new EntraLoginError('Entra non ha restituito un id_token.');
  await exchange(user.id_token, fetchFn);
  const state = user.state as { returnTo?: unknown } | undefined;
  const returnTo = typeof state?.returnTo === 'string' ? state.returnTo : '/';
  // Solo path interni: niente open redirect verso altri host.
  return returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/';
}

// Rinnovo single-flight: timer + 401 concorrenti condividono la stessa chiamata a Entra.
let inflight: Promise<string | null> | null = null;

/**
 * Rinnova il JWT applicativo con il refresh token Entra. null se non c'è una sessione Entra o il
 * rinnovo fallisce (il chiamante decide il logout). Non lancia mai.
 */
export function renewEntraSession(
  fetchFn: typeof fetch = globalThis.fetch,
): Promise<string | null> {
  if (getAuthMode() !== 'entra') return Promise.resolve(null);
  inflight ??= (async () => {
    try {
      const { entra } = await fetchSsoConfig(fetchFn);
      if (!entra) return null;
      const user = await userManager(entra).signinSilent();
      if (!user?.id_token) return null;
      return await exchange(user.id_token, fetchFn);
    } catch {
      return null;
    }
  })().finally(() => {
    inflight = null;
  });
  return inflight;
}

/** Logout locale: dimentica l'utente Entra (non chiude la sessione Microsoft dell'utente). */
export async function forgetEntraUser(): Promise<void> {
  try {
    await manager?.um.removeUser();
    // Anche senza un UserManager istanziato (es. dopo un reload): via le chiavi oidc dell'utente.
    for (const k of Object.keys(window.localStorage)) {
      if (k.startsWith('oidc.user:')) window.localStorage.removeItem(k);
    }
  } catch {
    /* storage non disponibile: niente da pulire */
  }
}

/** Login break-glass (admin locale): salva e restituisce il JWT applicativo. */
export async function breakGlassLogin(
  username: string,
  password: string,
  fetchFn: typeof fetch = globalThis.fetch,
): Promise<string> {
  const res = await fetchFn(authUrl('/break-glass'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const body = (await res.json().catch(() => ({}))) as { accessToken?: string; error?: string };
  if (!res.ok || !body.accessToken) {
    throw new EntraLoginError(body.error ?? `Accesso rifiutato (HTTP ${res.status})`);
  }
  setAuthMode('break-glass');
  setToken(body.accessToken);
  return body.accessToken;
}

/** Claim dell'id_token Entra della sessione corrente, come salvati da oidc-client-ts. */
export interface EntraProfile {
  claims: Record<string, unknown>;
  /** Scadenza dell'access token Entra (epoch s). */
  expiresAt?: number;
  scope?: string;
}

/**
 * Profilo Entra della sessione corrente (solo login Microsoft): claim dell'id_token letti
 * dall'utente oidc-client-ts in localStorage (`oidc.user:<authority>:<client_id>`). Sincrono,
 * nessuna chiamata di rete; null per gli altri login o se lo storage non è leggibile.
 */
export function readEntraProfile(): EntraProfile | null {
  if (getAuthMode() !== 'entra') return null;
  try {
    for (const key of Object.keys(window.localStorage)) {
      if (!key.startsWith('oidc.user:')) continue;
      const user = JSON.parse(window.localStorage.getItem(key) ?? 'null') as {
        profile?: Record<string, unknown>;
        expires_at?: number;
        scope?: string;
      } | null;
      if (!user?.profile) continue;
      return {
        claims: user.profile,
        ...(typeof user.expires_at === 'number' ? { expiresAt: user.expires_at } : {}),
        ...(typeof user.scope === 'string' ? { scope: user.scope } : {}),
      };
    }
  } catch {
    /* storage non disponibile o JSON corrotto */
  }
  return null;
}
